import { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Flame, Trophy } from "lucide-react";

const MEDALS = [
  { bg: "linear-gradient(135deg, #FFE08A, #F59E0B)", color: "#0A0A0B", glow: "rgba(245,158,11,0.75)" },
  { bg: "linear-gradient(135deg, #EDEDF0, #A8A8B0)", color: "#0A0A0B", glow: "rgba(220,220,228,0.6)" },
  { bg: "linear-gradient(135deg, #E0A36B, #B45309)", color: "#0A0A0B", glow: "rgba(205,127,50,0.65)" },
];

export default function Leaderboard() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/leaderboard").then((r) => setList(r.data)); }, []);

  if (!list) return <div className="max-w-[700px] mx-auto px-5 md:px-8 py-14"><div className="h-12 w-2/3 shimmer rounded" /></div>;

  return (
    <div className="max-w-[760px] mx-auto px-5 md:px-8 py-10 md:py-14">
      <motion.header
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-10"
      >
        <div className="eyebrow">This week</div>
        <h1 className="display-2 mt-2">
          <span className="grad-text">Leaderboard</span>
        </h1>
        <p className="text-[15px] text-[#B0AEBC] mt-3">
          Top studiers this week · Resets every Monday.
        </p>
      </motion.header>

      {list.length === 0 ? (
        <p className="text-[#8E8B99]">No data yet.</p>
      ) : (
        <div className="surface p-2">
          {list.map((row, i) => {
            const isTop3 = row.rank <= 3;
            const medal = isTop3 ? MEDALS[row.rank - 1] : null;
            return (
              <motion.div
                key={row.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.04 }}
                whileHover={{ x: 2 }}
                data-testid={`leaderboard-row-${row.rank}`}
                className={`flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all ${
                  row.is_you ? "bg-gradient-to-r from-[#7C5CFF]/10 via-[#B8A4FF]/5 to-transparent" : ""
                }`}
              >
                <motion.div
                  whileHover={{ scale: 1.1, rotate: isTop3 ? 8 : 0 }}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-bold shrink-0"
                  style={medal ? { background: medal.bg, color: medal.color, boxShadow: `0 0 20px -4px ${medal.glow}` } : { background: "rgba(255,255,255,0.06)", color: "#A1A1AA" }}
                >
                  {isTop3 ? <Trophy size={15} strokeWidth={2.3} /> : row.rank}
                </motion.div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[15px] truncate flex items-center gap-2">
                    {row.full_name}
                    {row.is_you && <span className="tag-soft">You</span>}
                  </div>
                  <div className="text-[12px] text-[#B0AEBC]">Level {row.level}</div>
                </div>
                <div className="flex items-center gap-1 text-[#B8A4FF]">
                  <Flame size={13} className="flame-flicker" fill="#B8A4FF" />
                  <span className="text-[13px] tabular-nums font-semibold">{row.streak_count}d</span>
                </div>
                <div className="text-right shrink-0 min-w-[68px]">
                  <div className="text-[20px] font-bold tabular-nums grad-text">{row.weekly_xp}</div>
                  <div className="text-[10px] text-[#8E8B99] font-medium">XP</div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
